import { inject, NgModule } from '@angular/core';
import {
	ActivatedRoute,
	ActivatedRouteSnapshot,
	NoPreloading,
	PreloadAllModules,
	RedirectFunction,
	Router,
	RouterModule,
	Routes,
} from '@angular/router';
import { environment } from 'src/environments/environment';
import { AppAdminGuard } from './app-admin/app-admin.guard';
import { ContactUsComponent } from './pages/contact-us/contact-us.component';
import { AppointmentBookingLandingComponent } from './pages/appointment-booking-landing/appointment-booking-landing.component';
import { AuthGuard } from './auth/auth.guard';
import { ContextService, UnauthenticatedContext } from './services/context.service';
import { CoreRoutes } from './app-core/core-routes';
import { AdminContext } from './app-admin/admin-context';
import { ClientContext } from './app-clients/client-context';
import { TenantContext } from './app-tenants/tenant-context';
import { ConsultantContext } from './app-consultants/consultant-context';
import { UsersService } from './services/users.service';

const canActivateRoot = (path: string) =>{
	return () => {
		const usersService = inject(UsersService);
		const contextService = inject(ContextService);
		const router = inject(Router);
		
		const context = contextService.currentContext;
		
		if(usersService.isLoggingOut){
			if(path.startsWith('login')){
				return true;
			}
			return router.parseUrl('/login')
		}
		
		if(context instanceof UnauthenticatedContext)
			return true;
		
		if(context instanceof AdminContext)
			return router.parseUrl('/admin/support-calendar')
		
		if(context instanceof ClientContext){
			console.log('redirecting...');
			if(context.permissions.viewAgenda){
				const agendaPath = `/${CoreRoutes.Clients}/${CoreRoutes.Agenda}`;
				return router.parseUrl(agendaPath);
			}
			else{
				return router.parseUrl(CoreRoutes.ClientsHomePath);
			}
		}
		
		if(context instanceof TenantContext)
			return router.parseUrl('/tenants/home')
		
		if(context instanceof ConsultantContext)
			return router.parseUrl('/consultants/home')
		
		const exhaustCheck: never = context;
		throw new Error("Unreachable code");
	}
}

const routes: Routes = [
    {
        path: 'admin',
        loadChildren: () => import('./app-admin/app-admin.module').then((m) => m.AppAdminModule),
        canActivate: [AppAdminGuard]
    },
    {
        path: '',
        pathMatch: 'full',
        redirectTo: (data) => {
			const res = canActivateRoot('')();
			if(res === true){
				return '/login';
			}
			return res;
		},
    },
    {
        path: 'logout',
        pathMatch: 'full',
        redirectTo: '/login',
    },
    {
        path: 'login',
        loadChildren: () => import('./auth/auth.module').then((m) => m.AuthPageModule),
		canActivate: [canActivateRoot('login')],
    },
    {
        path: 'register',
        loadChildren: () => import('./auth/registrazione/registrazione.module').then((m) => m.RegistrazionePageModule),
    },
    {
        path: 'reset-password',
        loadChildren: () =>
            import('./auth/reset-password/reset-password.module').then((m) => m.ResetPasswordPageModule),
    },
    {
        path: 'contact-us',
        component: ContactUsComponent
    },
    {
        path: 'prenota-appuntamento',
        component: AppointmentBookingLandingComponent
    },
    {
        path: 'clients',
        loadChildren: () => import('./app-clients/app-clients.routes').then((m) => m.APP_CLIENTS_ROUTES),
        canActivate: [AuthGuard],
    },
    {
        path: 'tenants',
        loadChildren: () => import('./app-tenants/app-tenants.routes').then((m) => m.APP_TENANTS_ROUTES),
    },
    {
        path: 'consultants',
        loadChildren: () => import('./app-consultants/app-consultants.routes').then((m) => m.APP_CONSULTANTS_ROUTES),
    },
    // {
    //     path: 'home',
    //     loadChildren: () => import('./app-clients/home/home.module').then((m) => m.HomePageModule),
    //     canActivate: [ConfigGuard],
    // },
    // {
    //     path: 'tabs',
    //     loadChildren: () => import('./components/tabs/tabs.module').then((m) => m.TabsModule),
    //     canActivate: [ConfigGuard],
    // },
    // {
    //     path: 'clienti',
    //     loadChildren: () => import('./app-admin/clients/clienti.module').then((m) => m.ClientiPageModule),
    //     canActivate: [ConfigGuard],
    // },
    // {
    //     path: 'collaboratori',
    //     loadChildren: () => import('./app-core/collaborators/collaboratori.module').then((m) => m.CollaboratoriPageModule),
    // },
    // {
    //     path: 'listino',
    //     loadChildren: () => import('./app-core/services/listino.module').then((m) => m.ListinoPageModule),
    // },
    // {
    //     path: 'magazzino',
    //     loadChildren: () => import('./app-core/magazzino/magazzino.module').then((m) => m.MagazzinoModule),
    // },
    // {
    //     path: 'promozioni',
    //     loadChildren: () => import('./app-core/promotions/promotions.module').then((m) => m.PromotionsModule),
    // },
    // {
    //     path: 'expenses',
    //     loadChildren: () => import('./app-core/expenses/expenses.module').then((m) => m.ExpensesModule),
    // },
    {
        path: 'privacy-policy',
        loadChildren: () => import('./pages/privacy-policy/privacy-policy.module').then((m) => m.PrivacyPolicyModule),
    },
    {
        path: 'tos',
        loadChildren: () => import('./app-clients/tos/tos.module').then((m) => m.TosModule),
    },
    {
        path: 'groups',
        loadChildren: () => import('./components/groups/groups.module').then((m) => m.GroupsModule),
    },
    {
        path: 'piva',
        loadChildren: () => import('./app-admin/piva/piva.module').then((m) => m.PivaModule),
    },
    {
        path: 'delete-account',
        loadChildren: () => import('./pages/delete-account/delete-account.module').then((m) => m.DeleteAccountModule),
    },
    
    // TODO to be removed
    // {
    //     path: 'sales-assistant',
    //     loadChildren: () => import('./components/sales-assistant/sales-assistant.module').then((m) => m.SalesAssistantPageModule),
    // },

    {
        path: '**',
        // redirectTo: 'tabs/' + environment.bottomTabs[0]
        loadChildren: () => import('./pages/not-found/not-found.routes').then((m) => m.NOT_FOUND_ROUTES),
    },
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, { preloadingStrategy: NoPreloading, enableTracing: !environment.production }),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule { }
